import React from 'react';
import PropTypes from 'prop-types';
import { 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';

const TourList = ({ 
  tours, 
  isLoading, 
  isError, 
  onEdit, 
  onDelete, 
  onToggleFeatured,
  sortField, 
  sortDirection, 
  onSort 
}) => {
  // Function to render sort indicator
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 ml-1" /> 
      : <ArrowDownIcon className="h-4 w-4 ml-1" />;
  };

  // Function to render sort button
  const renderSortButton = (field, label) => (
    <button
      onClick={() => onSort(field)}
      className="group inline-flex items-center text-left"
    >
      {label}
      <span className="ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
        {renderSortIcon(field)}
      </span>
    </button>
  );

  // Helper function to get location display value
  const getLocationDisplay = (tour) => {
    // Check if tour has a location object with a name property (new relationship)
    if (tour.location && typeof tour.location === 'object' && tour.location.name) {
      // Format with region and country if available
      let locationDisplay = tour.location.name;
      if (tour.location.region) {
        locationDisplay += ` - ${tour.location.region}`;
      }
      if (tour.location.country) {
        locationDisplay += `, ${tour.location.country}`;
      }
      return locationDisplay;
    }
    // If tour has a location string and not just a number, return it (legacy data)
    else if (typeof tour.location === 'string' && isNaN(tour.location)) {
      return tour.location;
    } 
    // If tour has a location string but looks like a number, it might be an ID - show as unknown
    else if (typeof tour.location === 'string' && !isNaN(tour.location)) {
      return 'Location data missing';
    }
    // If tour has a location number, it might be an ID - show as unknown
    else if (typeof tour.location === 'number') {
      return 'Location data missing';
    }
    // Default fallback
    return 'Unknown Location';
  };

  // Helper function to get group size display value
  const getGroupSizeDisplay = (tour) => {
    return tour.group_size || tour.groupSize || 0;
  };
  
  // Helper function to format price correctly regardless of whether it's a string or number
  const formatPrice = (price) => {
    // Convert to number if it's a string
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    // Check if conversion resulted in a valid number
    if (isNaN(numericPrice)) {
      return '$0.00';
    }
    
    // Format the price with dollar sign and commas
    return `$${numericPrice.toLocaleString()}`;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse p-6">
        <div className="h-10 bg-gray-200 rounded mb-4"></div>
        <div className="h-10 bg-gray-200 rounded mb-4"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-500 mb-4">Error loading tours. Please try again.</div>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!tours || tours.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500 mb-4">No tours found. Create your first tour to get started.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {renderSortButton('name', 'Tour Name')}
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {renderSortButton('location', 'Location')}
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {renderSortButton('duration', 'Duration')}
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {renderSortButton('price', 'Price')}
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {renderSortButton('group_size', 'Group Size')}
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {renderSortButton('is_featured', 'Featured')}
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {renderSortButton('is_active', 'Status')}
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              ACTIONS
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {tours.map((tour) => (
            <tr key={tour.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{tour.name}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">{getLocationDisplay(tour)}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">
                  {tour.duration} {tour.duration === 1 ? 'day' : 'days'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">{formatPrice(tour.price)}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">{getGroupSizeDisplay(tour)}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button 
                  onClick={() => onToggleFeatured(tour.id, tour.is_featured)}
                  className="text-sm text-gray-500 hover:text-yellow-500 focus:outline-none"
                  title={tour.is_featured ? "Remove from featured" : "Add to featured"}
                >
                  {tour.is_featured ? (
                    <StarIconSolid className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <StarIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  tour.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {tour.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end space-x-2">
                  <Link
                    to={`/tours/${tour.id}`}
                    target="_blank"
                    className="text-indigo-600 hover:text-indigo-900"
                    title="View"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </Link>
                  <button
                    onClick={() => onEdit(tour)}
                    className="text-blue-600 hover:text-blue-900"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => onDelete(tour.id)}
                    className="text-red-600 hover:text-red-900"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Results count message */}
      <div className="px-6 py-3 text-sm text-gray-500">
        Showing {tours.length} to {tours.length} of {tours.length} results
      </div>
    </div>
  );
};

TourList.propTypes = {
  tours: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      location: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      duration: PropTypes.number,
      price: PropTypes.number,
      is_featured: PropTypes.bool,
      is_active: PropTypes.bool
    })
  ),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleFeatured: PropTypes.func.isRequired,
  sortField: PropTypes.string,
  sortDirection: PropTypes.string,
  onSort: PropTypes.func
};

export default TourList;
